import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { Bar, Line } from "react-chartjs-2";
import { Chart, elements, registerables } from "chart.js";
import { BeatLoader } from "react-spinners";
import "../../../../../styles/count_graphs.css";
import {
  BiFullscreen,
  BiExitFullscreen,
  BiLineChart,
  BiBarChartAlt2,
} from "react-icons/bi";
import { peopleOccupancyColor, lprOccupancyColor } from "../../../../../config";

const WeeklyOccupancyChart = ({
  selectedYear,
  weeklyOccupancyData,
  weeklyDataLoading,
  selectedMonth,
  selectedWeek,
  relativeWeekNumber,
  handleWeeklyBarClick,
  weeklyPercentageIncrease,
  weeklyAlerts,
  occupancyType,
}) => {
  const [barChartData, setBarChartData] = useState();
  const [lineChartData, setLineChartData] = useState();
  const [chartType, setChartType] = useState("bar");
  const barChartRef = useRef(null);
  const lineChartRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const getMonthName = (monthNumber) => {
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return months[monthNumber - 1];
  };

  const smallScreen = window.innerWidth < 786;

  // Get the appropriate color based on occupancy type
  const getOccupancyColor = () => {
    return occupancyType === "LPR" ? lprOccupancyColor : peopleOccupancyColor;
  };

  const selectedWeekOfMonth = useMemo(() => {
    for (let i = 0; i < weeklyOccupancyData.length; i++) {
      if (weeklyOccupancyData[i]?.week === selectedWeek) {
        return i + 1;
      }
    }
  }, [weeklyOccupancyData, selectedWeek]);

  const prepareWeeklyOccupancyBarChart = () => {
    const color = getOccupancyColor();

    const relativeWeekLabels = [];

    for (let i = 1; i <= weeklyOccupancyData.length; i++) {
      relativeWeekLabels.push(i);
    }

    return {
      labels: relativeWeekLabels.map((d) => `Week ${d}`),
      datasets: [
        {
          label: "Weekly Occupancy Alerts",
          data: weeklyOccupancyData.map((d) => d.total_count),
          backgroundColor: color,
          borderColor: color,
          borderWidth: {
            top: 0,
            right: weeklyOccupancyData.map((d) =>
              selectedWeek === d.week ? 2 : 0
            ),
            bottom: 0,
            left: weeklyOccupancyData.map((d) =>
              selectedWeek === d.week ? 2 : 0
            ),
          },
        },
      ],
    };
  };

  const getWeeklyOccupancyBarChartOptions = () => {
    return {
      hover: {
        mode: "nearest",
        intersect: false,
      },
      onHover: function (event, elements) {
        const chartElement = this.canvas;
        if (elements && elements.length > 0) {
          chartElement.style.cursor = "pointer";
        } else {
          chartElement.style.cursor = "default";
        }
      },
      responsive: true, // Ensure the chart is responsive
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const index = elements[0].index;
          const weekNumber = weeklyOccupancyData[index].week;
          // console.log(
          //   "week number passed to weekly occupancy bar click handler ",
          //   weekNumber
          // );
          handleWeeklyBarClick(weekNumber);
        }
      },
      maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
      scales: {
        x: {
          stacked: true,
          ticks: {
            color: (context) => {
              // Change this to accomodate multiple polygons per month.
              const labelWeek = weeklyOccupancyData[context.index]?.week;
              return labelWeek === selectedWeek ? "#32de84" : "#fff";
            },
            font: {
              weight: (context) => {
                const labelWeek = weeklyOccupancyData[context.index]?.week;
                return labelWeek === selectedWeek ? "bold" : "normal";
              },
              size: (context) => {
                const labelWeek = weeklyOccupancyData[context.index]?.week;
                return labelWeek === selectedWeek ? 14 : 12;
              },
            },
          },
          title: {
            display: true,
            text: "Weeks",
            color: "white",
          },
          grid: {
            display: true,
            drawBorder: true,
            drawOnChartArea: false,
            color: "#aaa",
          },
        },
        y: {
          ticks: {
            color: "#F8F8F8",
          },
          stacked: false,
          title: {
            display: true,
            text: "Occupancy Alerts",
            color: "white",
          },
        },
      },
      plugins: {
        tooltip: {
          mode: "nearest", // Show tooltip for all datasets at the same index
        },
        legend: {
          position: "top", // Position the legend at the top
          labels: {
            color: "white",
            font: {
              size: smallScreen ? 8 : 14, // Adjust font size of legend labels
            },
            boxWidth: smallScreen ? 8 : 40, // Adjust width of legend marker box
            boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
          },
        },
      },
    };
  };

  const prepareWeeklyOccupancyLineChart = () => {
    const color = getOccupancyColor();

    const relativeWeekLabels = [];

    for (let i = 1; i <= weeklyOccupancyData.length; i++) {
      relativeWeekLabels.push(i);
    }

    return {
      labels: relativeWeekLabels.map((d) => `Week ${d}`),
      datasets: [
        {
          label: "Weekly Occupancy Alerts",
          data: weeklyOccupancyData.map((d) => d.total_count),
          backgroundColor: color,
          borderColor: color,
          borderWidth: 3,
          tension: 0.3,
        },
      ],
    };
  };

  const getWeeklyOccupancyLineChartOptions = () => {
    return {
      hover: {
        mode: "index",
        intersect: false,
      },
      onHover: function (event, elements) {
        const chartElement = this.canvas;
        if (elements && elements.length > 0) {
          chartElement.style.cursor = "pointer";
        } else {
          chartElement.style.cursor = "default";
        }
      },
      responsive: true, // Ensure the chart is responsive
      onClick: (event, elements) => {
        if (elements.length > 0) {
          const index = elements[0].index;
          const weekNumber = weeklyOccupancyData[index].week;
          // console.log(
          //   "week number passed to weekly occupancy bar click handler ",
          //   weekNumber
          // );
          handleWeeklyBarClick(weekNumber);
        }
      },
      elements: {
        points: {
          radius: 4,
          hoverRadius: 6,
        },
      },
      maintainAspectRatio: true, // Ensure the chart maintains its aspect ratio
      scales: {
        x: {
          ticks: {
            color: (context) => {
              // Change this to accomodate multiple polygons per month.
              const labelWeek = weeklyOccupancyData[context.index]?.week;
              return labelWeek === selectedWeek ? "#32de84" : "#fff";
            },
            font: {
              weight: (context) => {
                const labelWeek = weeklyOccupancyData[context.index]?.week;
                return labelWeek === selectedWeek ? "bold" : "normal";
              },
              size: (context) => {
                const labelWeek = weeklyOccupancyData[context.index]?.week;
                return labelWeek === selectedWeek ? 14 : 12;
              },
            },
          },
          title: {
            display: true,
            text: "Weeks",
            color: "white",
          },
          grid: {
            display: true,
            drawBorder: true,
            drawOnChartArea: false,
            color: "#aaa",
          },
        },
        y: {
          ticks: {
            color: "#F8F8F8",
          },
          title: {
            display: true,
            text: "Occupancy Alerts",
            color: "white",
          },
        },
      },
      plugins: {
        tooltip: {
          mode: "nearest", // Show tooltip for all datasets at the same index
        },
        legend: {
          position: "top", // Position the legend at the top
          labels: {
            color: "white",
            font: {
              size: smallScreen ? 8 : 14, // Adjust font size of legend labels
            },
            boxWidth: smallScreen ? 8 : 40, // Adjust width of legend marker box
            boxHeight: smallScreen ? 8 : 10, // Adjust height of legend marker box (optional, defaults to boxWidth)
          },
        },
      },
    };
  };

  const toggleChartType = () => {
    setChartType((prevType) => (prevType === "bar" ? "line" : "bar"));
  };

  useEffect(() => {
    if (weeklyOccupancyData.length) {
      const preparedBarChart = prepareWeeklyOccupancyBarChart();
      if (preparedBarChart) {
        setBarChartData(preparedBarChart);
      }
      const preparedLineChart = prepareWeeklyOccupancyLineChart();
      if (preparedLineChart) {
        setLineChartData(preparedLineChart);
      }
    }
  }, [weeklyOccupancyData]);

  Chart.register(...registerables);

  return (
    <div
      className={`count-bar-chart-wrapper ${
        isFullscreen ? "fullscreen-container" : ""
      }`}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        {/* Title aligned left */}
        <div
          className="graph-title"
          style={{ flex: "1 1 auto", minWidth: "200px" }}
        >
          Weekly {occupancyType} Occupancy
        </div>

        {/* Buttons and percentage aligned right */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            justifyContent: "flex-end",
            flex: "1 1 auto",
            minWidth: "100px",
          }}
        >
          {/* Chart toggle + fullscreen buttons */}
          <div style={{ display: "flex", gap: "10px" }}>
            <button className="toggle-chart-button" onClick={toggleChartType}>
              {chartType === "bar" ? (
                <BiLineChart
                  size={24}
                  title="Switch to Line Chart"
                  color="white"
                />
              ) : (
                <BiBarChartAlt2
                  size={24}
                  title="Switch to Bar Chart"
                  color="white"
                />
              )}
            </button>
            <button
              className="toggle-chart-button"
              onClick={() => setIsFullscreen((prev) => !prev)}
              title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            >
              {isFullscreen ? (
                <BiExitFullscreen size={24} color="white" />
              ) : (
                <BiFullscreen size={24} color="white" />
              )}
            </button>
          </div>

          {/* Percentage change */}
          <div className="count-chart-footer-percentage-container">
            <div className="count-chart-footer-percentage-icon">
              {weeklyPercentageIncrease > 0 ? (
                <svg
                  style={{ color: "#32de84" }}
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-arrow-up-right-circle"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 10.803a.5.5 0 1 1-.708-.707L9.243 6H6.475a.5.5 0 1 1 0-1h3.975a.5.5 0 0 1 .5.5v3.975a.5.5 0 1 1-1 0V6.707l-4.096 4.096z"
                  />
                </svg>
              ) : (
                <svg
                  style={{ color: "#EF5B5B" }}
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  className="bi bi-arrow-down-right-circle"
                  viewBox="0 0 16 16"
                >
                  <path
                    fillRule="evenodd"
                    d="M1 8a7 7 0 1 0 14 0A7 7 0 0 0 1 8zm15 0A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.854 5.146a.5.5 0 1 0-.708.708L9.243 9.95H6.475a.5.5 0 1 0 0 1h3.975a.5.5 0 0 0 .5-.5V6.475a.5.5 0 1 0-1 0v2.768L5.854 5.146z"
                  />
                </svg>
              )}
            </div>
            <div
              className="count-chart-footer-percentage-value"
              style={{
                color: weeklyPercentageIncrease > 0 ? "#32de84" : "#EF5B5B",
              }}
            >
              {weeklyPercentageIncrease}%
            </div>
          </div>
        </div>
      </div>
      <div className="chart-total-container">
        <div className="chart-total-pill">
          <div
            className="chart-total-label"
            style={{ color: getOccupancyColor() }}
          >
            Total:{" "}
            {weeklyOccupancyData.reduce(
              (acc, curr) => acc + curr.total_count,
              0
            )}
          </div>
        </div>
      </div>
      <div
        className={
          weeklyDataLoading ? "monthly-bar-chart-container-loading" : ""
        }
      >
        {chartType === "bar" ? (
          <Bar
            ref={barChartRef}
            data={barChartData || { labels: [], datasets: [] }} // Fetch prepared data
            options={getWeeklyOccupancyBarChartOptions()}
          />
        ) : (
          <Line
            ref={lineChartRef}
            data={lineChartData}
            options={getWeeklyOccupancyLineChartOptions()}
          />
        )}
        {/* <div className='chart-footer-container'>
                    <div className='chart-footer-label'>{weeklyAlerts} alerts for week {selectedWeekOfMonth} of {getMonthName(selectedMonth)} {selectedYear}</div>
                </div> */}
        {weeklyDataLoading && (
          <div id="monthly-loading-overlay">
            <BeatLoader color={"white"} loading={weeklyDataLoading} size={15} />
          </div>
        )}
      </div>
    </div>
  );
};
export default WeeklyOccupancyChart;
